import { useState, type FormEvent } from 'react';
import { Send } from 'lucide-react';
import { Button, Card, Notice, Select, Textarea, useToast } from '../ui';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';

interface Contato { id: string; waId: string; profileName: string | null }
interface Telefone { id: string; displayNumber: string }
interface Canal { id: string; phoneNumbers: Telefone[] }

/**
 * Envio de teste.
 *
 * São duas chamadas encadeadas: `POST /conversations` abre (ou reaproveita) a
 * conversa, e só então `POST /conversations/:id/messages` envia. O front
 * anterior tratava as duas como uma só e, quando a segunda falhava, deixava uma
 * conversa vazia criada e dizia "mensagem enviada".
 *
 * Aqui os dois passos são distintos na mensagem de erro, porque a ação de
 * recuperação é diferente: falha ao abrir é dado inválido; falha ao enviar
 * costuma ser janela de 24 horas fechada.
 */
export function TestarMensagem() {
  const toast = useToast();
  const contatos = useResource<Contato[]>('/workspace/contacts');
  const canais = useResource<Canal[]>('/workspace/channels');

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const opcoesContato = contatos.state.status === 'ready'
    ? contatos.state.data.map((c) => ({ value: c.id, label: `${c.profileName ?? 'Sem nome'} — ${c.waId}` }))
    : [];
  const opcoesNumero = canais.state.status === 'ready'
    ? canais.state.data.flatMap((c) => c.phoneNumbers.map((t) => ({ value: t.id, label: t.displayNumber })))
    : [];

  const carregando = contatos.state.status === 'loading' || canais.state.status === 'loading';
  const semPreRequisito = !carregando && (opcoesContato.length === 0 || opcoesNumero.length === 0);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const contactId = String(form.get('contactId') ?? '');
    const phoneNumberId = String(form.get('phoneNumberId') ?? '');
    const body = String(form.get('body') ?? '').trim();
    setErro(null);
    setEnviando(true);
    try {
      const conversa = await api<{ id: string }>('/workspace/conversations', {
        method: 'POST',
        body: { contactId, phoneNumberId },
      });
      try {
        await api(`/workspace/conversations/${conversa.id}/messages`, {
          method: 'POST',
          body: { kind: 'text', body },
        });
      } catch (falha) {
        throw new Error(
          falha instanceof ApiError
            ? `A conversa foi aberta, mas a mensagem não saiu: ${falha.message}`
            : 'A conversa foi aberta, mas a mensagem não saiu.',
        );
      }
      toast('Mensagem enviada.');
      (event.target as HTMLFormElement).reset();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card
      title="Enviar mensagem de teste"
      subtitle="Abre uma conversa com o contato escolhido e envia um texto pelo número selecionado."
    >
      {semPreRequisito ? (
        <Notice tone="warning" title="Falta pré-requisito">
          {opcoesNumero.length === 0
            ? 'Nenhum número conectado. Conecte um número antes de testar o envio.'
            : 'Nenhum contato cadastrado. Cadastre um contato antes de testar o envio.'}
        </Notice>
      ) : (
        <form onSubmit={enviar} style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '34rem' }}>
          {erro && <Notice tone="danger" title="Não foi possível enviar" live>{erro}</Notice>}
          <Select
            name="contactId"
            label="Contato"
            required
            placeholder="Selecione o contato"
            options={opcoesContato}
            disabled={carregando}
          />
          <Select
            name="phoneNumberId"
            label="Enviar pelo número"
            required
            placeholder="Selecione o número"
            options={opcoesNumero}
            disabled={carregando}
          />
          <Textarea
            name="body"
            label="Mensagem"
            required
            rows={4}
            hint="Fora da janela de 24 horas só é possível enviar modelo aprovado, não texto livre."
          />
          <div>
            <Button
              variant="primary"
              type="submit"
              loading={enviando}
              loadingLabel="Enviando mensagem"
              icon={<Send size={18} aria-hidden="true" />}
            >
              Enviar
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
